import { compareMessages } from "./messageOrder";

// Shared message utilities for normalizing messages across Dashboard and Extension
// This ensures consistent handling of message formats in both contexts

/**
 * Recursively parses JSON strings in nested structures.
 * Handles cases where values might be JSON strings that need parsing.
 * 
 * @param value - The value to parse (can be string, object, array, or primitive)
 * @returns Parsed value with all JSON strings converted to objects
 */
function parseRecursiveJson(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }
  
  // If it's a string, only unwrap doubly-encoded JSON objects/arrays. Leave scalar strings
  // (e.g. a numeric id like "92800403272319907") intact so JSON.parse doesn't coerce them to a
  // precision-losing Number and round large integers past 2^53.
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return parseRecursiveJson(JSON.parse(value));
      } catch {
        // Not valid JSON, return as-is
        return value;
      }
    }
    return value;
  }
  
  // If it's an array, recursively parse each element
  if (Array.isArray(value)) {
    return value.map(item => parseRecursiveJson(item));
  }
  
  // If it's an object, recursively parse each property
  if (typeof value === "object") {
    const result: any = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = parseRecursiveJson(val);
    }
    return result;
  }
  
  // Primitive value, return as-is
  return value;
}

function toArray<T>(value: T | T[] | null | undefined): T[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function hasValue(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function isLikelyHumanReviewPayload(candidate: any): boolean {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return false;

  const infoReq = candidate.information_request ?? candidate.informationRequest;
  if (Array.isArray(candidate.tools) && candidate.tools.length > 0) return true;
  if (Array.isArray(candidate.plans) && candidate.plans.length > 0) return true;
  if (Array.isArray(infoReq) && infoReq.length > 0) return true;

  const markerKeys = [
    "tool_name",
    "tool_description",
    "args_json",
    "call_id",
    "tool_action",
    "plan_name",
    "plan_description",
    "plan_steps",
    "plan_url",
    "selected_plan_name",
  ];
  return markerKeys.some((key) => hasValue(candidate[key]));
}

function pickValue<T>(values: T[], index: number): T | undefined {
  if (values[index] !== undefined) return values[index];
  if (values.length === 1) return values[0];
  return undefined;
}

function normalizeUpdatedArgPaths(value: unknown): string[] {
  if (typeof value === "string" && value.length > 0) return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

export function extractExportActionName(content: unknown): string {
  const fromObject = (value: any): string => {
    if (!value || typeof value !== "object") return "";
    const direct = typeof value.action_name === "string" ? value.action_name.trim() : "";
    if (direct) return direct;
    const camel = typeof value.actionName === "string" ? value.actionName.trim() : "";
    if (camel) return camel;
    const nested = value.export_action_request;
    if (nested && typeof nested === "object") {
      const nestedDirect =
        typeof nested.action_name === "string" ? nested.action_name.trim() : "";
      if (nestedDirect) return nestedDirect;
      const nestedCamel =
        typeof nested.actionName === "string" ? nested.actionName.trim() : "";
      if (nestedCamel) return nestedCamel;
    }
    const nestedContent = value.content;
    if (typeof nestedContent === "string" && nestedContent.trim()) {
      return extractExportActionName(nestedContent);
    }
    return "";
  };

  if (content && typeof content === "object") {
    return fromObject(content);
  }

  const raw = typeof content === "string" ? content.trim() : "";
  if (!raw) return "";

  try {
    const parsed = JSON.parse(raw);
    const parsedAction = fromObject(parsed);
    if (parsedAction) return parsedAction;
  } catch {
    // Ignore JSON parse errors; payload may be protobuf text.
  }

  const actionMatch = raw.match(/action_name:\s*"([^"]+)"/);
  if (actionMatch?.[1]) return actionMatch[1].trim();

  const actionUnquotedMatch = raw.match(/action_name:\s*([A-Za-z0-9_./-]+)/);
  if (actionUnquotedMatch?.[1]) return actionUnquotedMatch[1].trim();

  return "";
}

export function formatExportRequestForDisplay(content: unknown): string {
  const actionName = extractExportActionName(content);
  if (actionName) return `Export ${actionName}`;

  if (typeof content === "string") {
    const raw = content.trim();
    if (/^export\s+/i.test(raw)) return raw;
    if (raw) return raw;
  }

  return "Export request";
}

export function normalizeHumanReviewPayload(humanReview: any): any {
  if (!humanReview || typeof humanReview !== "object") return humanReview;

  const base = {
    ...humanReview,
    args_json: humanReview.args_json !== undefined ? parseRecursiveJson(humanReview.args_json) : humanReview.args_json,
    original_args_json: humanReview.original_args_json !== undefined
      ? parseRecursiveJson(humanReview.original_args_json)
      : humanReview.original_args_json,
    updated_arg_paths: humanReview.updated_arg_paths,
    arg_types_json: humanReview.arg_types_json !== undefined ? parseRecursiveJson(humanReview.arg_types_json) : humanReview.arg_types_json,
    resolved_args: humanReview.resolved_args !== undefined ? parseRecursiveJson(humanReview.resolved_args) : humanReview.resolved_args,
    information_request: humanReview.information_request !== undefined
      ? parseRecursiveJson(humanReview.information_request)
      : humanReview.information_request,
    steps_taken: humanReview.steps_taken !== undefined
      ? parseRecursiveJson(humanReview.steps_taken)
      : humanReview.steps_taken,
  };

  const toolNames = toArray(base.tool_name);
  const toolDescriptions = toArray(base.tool_description);
  const argsJson = toArray(base.args_json);
  const argTypes = toArray(base.arg_types_json);
  const resolvedArgs = toArray(base.resolved_args);
  const callIds = toArray(base.call_id);
  const toolActions = toArray(base.tool_action);
  const isUndoableArr = toArray(base.is_undoable);
  const originalArgsJson = toArray(base.original_args_json);
  const updatedArgPaths = toArray(base.updated_arg_paths);
  const maxToolLength = Math.max(
    toolNames.length,
    toolDescriptions.length,
    argsJson.length,
    argTypes.length,
    resolvedArgs.length,
    callIds.length,
    toolActions.length,
    originalArgsJson.length,
    updatedArgPaths.length
  );

  const tools = maxToolLength > 0
    ? Array.from({ length: maxToolLength }, (_, index) => ({
        tool_name: pickValue(toolNames, index),
        tool_description: pickValue(toolDescriptions, index),
        args_json: pickValue(argsJson, index),
        original_args_json: pickValue(originalArgsJson, index),
        updated_arg_paths: normalizeUpdatedArgPaths(pickValue(updatedArgPaths, index)),
        arg_types_json: pickValue(argTypes, index),
        resolved_args: pickValue(resolvedArgs, index),
        call_id: pickValue(callIds, index),
        action: pickValue(toolActions, index),
        is_undoable: pickValue(isUndoableArr, index) === true,
      })).filter((tool) =>
        hasValue(tool.tool_name) ||
        hasValue(tool.tool_description) ||
        hasValue(tool.args_json) ||
        hasValue(tool.call_id)
      )
    : [];

  const planNames = toArray(base.plan_name);
  const planDescriptions = toArray(base.plan_description);
  const planSteps = toArray(base.plan_steps);
  const planUrls = toArray(base.plan_url);
  const maxPlanLength = Math.max(
    planNames.length,
    planDescriptions.length,
    planSteps.length,
    planUrls.length
  );

  const plans = maxPlanLength > 0
    ? Array.from({ length: maxPlanLength }, (_, index) => ({
        plan_name: pickValue(planNames, index),
        plan_description: pickValue(planDescriptions, index),
        steps: pickValue(planSteps, index),
        url: pickValue(planUrls, index),
      }))
        .map((plan) => ({
          ...plan,
          steps: plan.steps !== undefined ? parseRecursiveJson(plan.steps) : plan.steps,
        }))
        .filter((plan) =>
          hasValue(plan.plan_name) ||
          hasValue(plan.plan_description) ||
          hasValue(plan.steps) ||
          hasValue(plan.url)
        )
    : [];

  return {
    ...base,
    tools,
    plans,
    information_request: Array.isArray(base.information_request)
      ? base.information_request
      : hasValue(base.information_request)
      ? [base.information_request]
      : [],
    steps_taken: Array.isArray(base.steps_taken)
      ? base.steps_taken
      : hasValue(base.steps_taken)
      ? [base.steps_taken]
      : [],
  };
}

export function extractHumanReviewPayloadFromContent(content: unknown): any | null {
  const tryNormalize = (candidate: any): any | null => {
    if (!candidate || typeof candidate !== "object") return null;
    const source =
      candidate.human_review && typeof candidate.human_review === "object"
        ? candidate.human_review
        : candidate;
    if (!isLikelyHumanReviewPayload(source)) return null;
    return normalizeHumanReviewPayload(source);
  };

  if (content && typeof content === "object") {
    return tryNormalize(content);
  }

  if (typeof content !== "string") return null;

  const trimmed = content.trim();
  if (!trimmed) return null;

  const candidates: string[] = [trimmed];
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
    if (jsonMatch?.[0]) candidates.push(jsonMatch[0]);
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      const normalized = tryNormalize(parsed);
      if (normalized) return normalized;
    } catch {
      // Ignore parse errors; payload may be plain text.
    }
  }

  return null;
}

export interface ChatMessage {
  message_id: string;
  ai_thread_id: string;
  user_id: string;
  content: string;
  content_type?: string;
  created_at: { seconds: string; nanos: number };
  pending?: boolean;
  isStreaming?: boolean;
  status?: 'pending' | 'sent' | 'confirmed' | 'failed';
  error?: string;
  serverMessageId?: string;
  requestId?: string;
  entry_id?: string;
  stream_entry_ids?: string[];
  /** Set on messages that belong to an agent run. Empty for a technician's own chat. */
  agent_display_name?: string | null;
  human_review?: {
    tool_name?: string | string[];
    tool_description?: string | string[];
    args_json?: string | object | (string | object)[];
    resolved_args?: any | any[];
    call_id?: string | string[];
    arg_types_json?: any | any[];
    plan_name?: string | string[];
    plan_description?: string | string[];
    plan_steps?: any[] | any[][];
    plan_id?: string | string[];
    plan_url?: string | string[];
    tools?: Array<{
      tool_name?: string;
      tool_description?: string;
      args_json?: string | object;
      original_args_json?: Record<string, unknown>;
      updated_arg_paths?: string[];
      resolved_args?: any;
      call_id?: string;
      arg_types_json?: any;
    }>;
    plans?: Array<{
      plan_name?: string;
      plan_description?: string;
      steps?: any[];
      url?: string;
    }>;
    information_request?: Array<{
      id?: string;
      question?: string;
      form_type?: string;
      suggested_answer?: any;
    }>;
    steps_taken?: string[];
    tool_action?: string | string[] | null;
    information_answers?: Record<string, string>;
    pdf_export_json?: string | { value?: string } | null;
  };
  human_review_status?: string;
  human_review_response?: string;
  human_review_ack?: string | { content: string; user_id?: string | null; user_name?: string | null };
  __ack_to_insert?: any;
  isHistory?: boolean;
  isComplete?: boolean;
}

export function normalizeMessages(messages: any[]): ChatMessage[] {
  const normalizedMessages = messages.map((msg) => {
    // Preserve ack fields for all messages
    const preserveAckFields = {
      human_review_ack: msg.human_review_ack,
      __ack_to_insert: msg.__ack_to_insert,
    };

    if (msg.content_type === "EXPORT_REQUEST") {
      return {
        ...msg,
        content: formatExportRequestForDisplay(msg.content),
        ...preserveAckFields,
      };
    }

    // Tool invocation message (from assistant) - but NOT server-stored human review
    if (
      msg.user_id === "neo" &&
      typeof msg.content === "string" &&
      msg.content.trim().startsWith("{") &&
      msg.content.includes("toolName") &&
      msg.content_type !== "HUMAN_REVIEW"
    ) {
      try {
        const contentData = JSON.parse(msg.content);
        return {
          ...msg,
          content: "",
          human_review: normalizeHumanReviewPayload({
            tool_name: contentData.toolName,
            tool_description: contentData.toolDescription,
            args_json: parseRecursiveJson(contentData.argsJson),
            arg_types_json: parseRecursiveJson(contentData.argTypesJson),
            steps_taken: contentData.steps_taken || [],
            plan_name: contentData.planName || contentData.plan_name,
            plan_description: contentData.planDescription || contentData.plan_description,
            plan_steps: contentData.planSteps || contentData.plan_steps,
            plan_id: contentData.planId || contentData.plan_id,
            plan_url: contentData.planUrl || contentData.plan_url,
          }),
          ...preserveAckFields,
        };
      } catch (error) {
        return { ...msg, ...preserveAckFields };
      }
    }

    // User HITL response message - handle JSON format from chat assistant threads
    if (
      msg.user_id !== "neo" &&
      typeof msg.content === "string" &&
      msg.content.trim().startsWith("{")
    ) {
      try {
        const json = JSON.parse(msg.content);
        const tools = json.tools || [];
        const plans = json.plans || [];
        const informationResponse = json.information_response || json.informationResponse || [];

        if (tools.length > 0 || plans.length > 0 || informationResponse.length > 0) {
          const actions = [
            ...tools.map((t: any) => t.action || "CONTINUE"),
            ...plans.map((p: any) => p.action || "CONTINUE"),
            ...informationResponse.map((r: any) => r.action || "CONTINUE"),
          ];

          const hasCancel = actions.some((a: string) => a === "CANCEL" || a === "canceled");
          const hasScheduled = actions.some((a: string) => a === "SCHEDULED" || a === "scheduled");
          const hasUpdate = actions.some((a: string) => a === "UPDATE" || a === "update");
          const action = hasCancel ? "canceled" : hasScheduled ? "scheduled" : hasUpdate ? "updated" : "approved";

          return {
            ...msg,
            content: `You ${action} this action.`,
            human_review_response: action,
            content_type: "HUMAN_REVIEW",
            ...preserveAckFields,
          };
        }
      } catch (error) {
        // Failed to parse JSON, fall through to next handler
      }
    }

    // Handle messages with content_type "HUMAN_REVIEW" from server (neo)
    if (msg.user_id === "neo" && msg.content_type === "HUMAN_REVIEW" && msg.human_review) {
      const contentHumanReview = extractHumanReviewPayloadFromContent(msg.content);
      const mergedHumanReview = {
        ...(contentHumanReview || {}),
        ...msg.human_review,
        pdf_export_json:
          msg.human_review.pdf_export_json ??
          contentHumanReview?.pdf_export_json,
      };
      return {
        ...msg,
        content: "",
        human_review: normalizeHumanReviewPayload(mergedHumanReview),
        ...preserveAckFields,
      };
    }

    // User action message - handle various formats
    if (
      typeof msg.content === "string" &&
      (msg.content.includes("action: CONTINUE") ||
        msg.content.includes("action: APPROVE") ||
        msg.content.includes("action: UPDATE") ||
        msg.content.includes("action: CANCEL") ||
        msg.content.includes("action: SCHEDULED") ||
        msg.content.match(/tools\s*\{\s*action:\s*(CONTINUE|APPROVE|UPDATE|CANCEL|SCHEDULED)/i) ||
        msg.content.match(/plans\s*\{\s*action:\s*(CONTINUE|APPROVE|UPDATE|CANCEL|SCHEDULED)/i) ||
        msg.content.match(/information_response\s*\{\s*action:\s*(CONTINUE|APPROVE|UPDATE|CANCEL|SCHEDULED)/i))
    ) {
      const action = msg.content.includes("CANCEL") ? "canceled" : msg.content.includes("SCHEDULED") ? "scheduled" : "approved";
      return {
        ...msg,
        content: `You ${action} this action.`,
        human_review_response: action,
        content_type: msg.content_type || "HUMAN_REVIEW",
        ...preserveAckFields,
      };
    }

    // Ensure content_type is always set
    if (!msg.content_type) {
      return {
        ...msg,
        content_type: "MESSAGE",
        ...preserveAckFields,
      };
    }

    return { ...msg, ...preserveAckFields };
  }).map((msg) => ({
    ...msg,
    ...(msg.message_id != null ? { message_id: String(msg.message_id) } : {}),
    ...(msg.ai_thread_id != null ? { ai_thread_id: String(msg.ai_thread_id) } : {}),
  }));

  // Deduplicate messages by message_id (if present)
  const seenMessageIds = new Set<string>();
  const deduplicatedMessages = normalizedMessages.filter((msg) => {
    if (msg.message_id) {
      const messageId = String(msg.message_id);
      if (seenMessageIds.has(messageId)) {
        return false; // Duplicate, skip it
      }
      seenMessageIds.add(messageId);
    }
    return true; // Keep message (either has no message_id or is first occurrence)
  });

  // Sort messages by timestamp (oldest first)
  const sortedMessages = deduplicatedMessages.sort(compareMessages);

  // Combine consecutive plan step messages (those starting with "◆ Step") into a single message
  const combinedMessages: ChatMessage[] = [];
  for (let i = 0; i < sortedMessages.length; i++) {
    const currentMsg = sortedMessages[i];
    const isPlanStep = currentMsg.user_id === "neo" && 
                       typeof currentMsg.content === "string" && 
                       (currentMsg.content.trim().startsWith("▶ Step") || 
                        currentMsg.content.trim().startsWith("→ Step") || 
                        currentMsg.content.trim().startsWith("◆ Step"));
    
    if (isPlanStep) {
      // Start accumulating plan steps
      let combinedContent = currentMsg.content;
      let combinedMessage = { ...currentMsg };
      let j = i + 1;
      
      // Look ahead to find consecutive plan steps
      while (j < sortedMessages.length) {
        const nextMsg = sortedMessages[j];
        const isNextPlanStep = nextMsg.user_id === "neo" && 
                               typeof nextMsg.content === "string" && 
                               (nextMsg.content.trim().startsWith("▶ Step") ||
                                nextMsg.content.trim().startsWith("→ Step") || 
                                nextMsg.content.trim().startsWith("◆ Step"));
        
        if (isNextPlanStep) {
          // Combine with double newline separator (markdown paragraph break)
          // Ensure proper spacing for markdown rendering
          const nextContent = nextMsg.content.trim();
          combinedContent += "\n\n" + nextContent;
          // Use the first message's metadata but update content
          combinedMessage = {
            ...combinedMessage,
            content: combinedContent,
            // Keep the earliest timestamp
            created_at: combinedMessage.created_at,
          };
          j++;
        } else {
          break; // Not a plan step, stop accumulating
        }
      }
      
      // Add the combined message and skip the individual steps we've combined
      combinedMessages.push(combinedMessage);
      i = j - 1; // Skip to after the last combined step
    } else {
      // Not a plan step, add as-is
      combinedMessages.push(currentMsg);
    }
  }

  return combinedMessages;
}

/**
 * Collapse consecutive streaming AI chunks into a single display message.
 * Each chunk is stored as its own message in state so React sees array length change and re-renders;
 * for display we merge only chunks that belong to the same streaming message_id
 * (plus an optional empty stream-end marker for that same message).
 */
export function collapseStreamingChunks<
  T extends { user_id?: string; isStreaming?: boolean; content?: string; created_at?: unknown },
>(messages: T[]): T[] {
  if (!messages?.length) return messages;
  const out: T[] = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i];
    if (m.user_id === "neo" && m.isStreaming) {
      let content = m.content ?? "";
      let j = i + 1;
      let anyStillStreaming = m.isStreaming;
      const streamMessageId = (m as any).message_id ?? null;
      while (j < messages.length) {
        const next = messages[j] as T;
        if (next.user_id !== "neo") break;
        const nextMessageId = (next as any).message_id ?? null;
        const isSameStreamChunk =
          next.isStreaming === true &&
          (streamMessageId == null || nextMessageId == null || String(nextMessageId) === String(streamMessageId));
        const isStreamEndMarker =
          next.isStreaming !== true &&
          (next.content ?? "") === "" &&
          streamMessageId != null &&
          nextMessageId != null &&
          String(nextMessageId) === String(streamMessageId);
        if (!isSameStreamChunk && !isStreamEndMarker) break;
        content += next.content ?? "";
        if (next.isStreaming) anyStillStreaming = true;
        j++;
      }
      // Preserve isStreaming for scroll/UX (e.g. auto vs smooth scroll, typing state).
      out.push({ ...m, content, isStreaming: anyStillStreaming, created_at: m.created_at } as T);
      i = j;
    } else {
      out.push(m);
      i++;
    }
  }
  return out;
}

/**
 * Group consecutive plan step messages into a single display message block.
 */
export function groupConsecutivePlanSteps<
  T extends { user_id?: string; content?: string; created_at?: unknown },
>(messages: T[]): T[] {
  if (!messages?.length) return messages;
  const out: T[] = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i] as T;
    const isPlanStep =
      m.user_id === "neo" &&
      typeof m.content === "string" &&
      (m.content.trim().startsWith("▶ Step") ||
        m.content.trim().startsWith("→ Step") ||
        m.content.trim().startsWith("◆ Step"));

    if (!isPlanStep) {
      out.push(m);
      i++;
      continue;
    }

    let combinedContent = m.content ?? "";
    let combinedMessage = { ...m };
    let j = i + 1;
    while (j < messages.length) {
      const next = messages[j] as T;
      const isNextPlanStep =
        next.user_id === "neo" &&
        typeof next.content === "string" &&
        (next.content.trim().startsWith("▶ Step") ||
          next.content.trim().startsWith("→ Step") ||
          next.content.trim().startsWith("◆ Step"));
      if (!isNextPlanStep) break;
      const nextContent = (next.content ?? "").trim();
      combinedContent += "\n\n" + nextContent;
      combinedMessage = {
        ...combinedMessage,
        content: combinedContent,
        created_at: combinedMessage.created_at,
      };
      j++;
    }
    out.push(combinedMessage as T);
    i = j;
  }
  return out;
}
